"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Calculator, Stethoscope, Scale, Wrench, Zap, ShoppingBag, Building2, Compass, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const sectorsData = [{
  id: "accountants",
  title: "Accountants & Financial Advisers",
  description: "Capturing high-intent commercial searches for audit, tax planning, and corporate accountancy retainers.",
  icon: Calculator,
  badge: "B2B & Finance"
}, {
  id: "solicitors",
  title: "Solicitors & Legal Practices",
  description: "Driving qualified legal inquiries for family law, conveyancing, corporate law, and commercial litigation.",
  icon: Scale,
  badge: "Legal"
}, {
  id: "dentists",
  title: "Dentists & Private Healthcare",
  description: "Attracting high-value cosmetic treatments, dental implants, and private patient consultations locally.",
  icon: Stethoscope,
  badge: "Healthcare"
}, {
  id: "plumbers",
  title: "Plumbers & Heating Engineers",
  description: "Dominating local search results for urgent repairs, boiler installations, and commercial maintenance.",
  icon: Wrench,
  badge: "Trade Services"
}, {
  id: "electricians",
  title: "Electricians & Electrical Contractors",
  description: "Targeting high-margin commercial fit-outs, EV charger setups, and local residential contracts.",
  icon: Zap,
  badge: "Trade Services"
}, {
  id: "ecommerce",
  title: "E-commerce & Retail Brands",
  description: "Optimising technical architecture, faceted navigation, and high-converting product landing pages.",
  icon: ShoppingBag,
  badge: "E-commerce"
}, {
  id: "b2b-saas",
  title: "B2B SaaS & Tech Companies",
  description: "Accelerating demo signups and trial downloads through targeted bottom-of-funnel search strategies.",
  icon: Building2,
  badge: "Technology"
}, {
  id: "architects",
  title: "Architects & Property Surveyors",
  description: "Positioning your practice for high-end residential, commercial developments, and planning projects.",
  icon: Compass,
  badge: "Property & Design"
}];
export default function Sectors() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="sectorsservedgrid" data-nav="light" className="relative bg-background py-20 lg:py-28 text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <Badge variant="secondary" className="mb-4 text-xs font-semibold uppercase tracking-wider px-3 py-1">
            Industry Expertise
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-foreground tracking-tight mb-6">
            Sectors Tailored for Sustainable Organic Growth
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Every market has distinct search behaviors and competitive dynamics. Working directly with me ensures an SEO strategy custom-engineered for your industry's customer journey.
          </p>
        </div>

        {/* Sectors Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {sectorsData.map((sector, i) => {
          const IconComponent = sector.icon;
          return <motion.div key={sector.id} variants={itemVariants} data-index={i}>
                <Card className="h-full flex flex-col justify-between border border-border bg-card hover:border-primary/40 transition-[border-color,box-shadow] duration-300 shadow-sm hover:shadow-md">
                  <CardHeader className="p-6 pb-2">
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary shrink-0">
                        <IconComponent className="w-6 h-6" />
                      </div>
                      <Badge variant="outline" className="text-xs font-normal border-border">
                        {sector.badge}
                      </Badge>
                    </div>
                    <CardTitle className="text-lg font-semibold text-card-foreground leading-snug">
                      {sector.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-6 pt-2 flex-1 flex flex-col justify-between gap-4">
                    <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                      {sector.description}
                    </CardDescription>
                    <div className="pt-2 flex items-center text-xs font-medium text-primary">
                      <CheckCircle2 className="w-4 h-4 mr-1.5 text-primary shrink-0" />
                      <span>Direct consultant focus</span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Bottom CTA Card */}
        <div className="mt-16 rounded-2xl bg-muted/50 border border-border p-8 sm:p-10 lg:p-12 flex flex-col lg:flex-row items-center justify-between gap-8">
          <div className="max-w-2xl text-center lg:text-left">
            <h3 className="text-xl sm:text-2xl font-semibold text-foreground mb-3">
              Don't See Your Specific Industry Listed?
            </h3>
            <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
              SEO core principles remain consistent, but execution demands unique context. I analyze search intent, buyer paths, and competitive gaps for any specialized niche.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 shrink-0 w-full lg:w-auto">
            <Button size="lg" className="w-full sm:w-auto font-medium" asChild>
              <Link to="/ContactPage">
                Book a Free SEO Consultation
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
            <Button variant="outline" size="lg" className="w-full sm:w-auto font-medium" asChild>
              <Link to="/CaseStudiesPage">
                Explore Case Studies
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}